import { useState, useCallback, useEffect, useRef } from "react"
import { Badge, Box, Button, HStack, Spinner, Stack, Table, Text, VStack, Center } from "@chakra-ui/react"
import { Alert } from "@hydrologiq/shared/chakra/Alert"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useFeatureToggles } from "../../hooks/useFeatureToggles"
import { FaToggleOn, FaToggleOff } from "react-icons/fa6"
import { FaInfoCircle } from "react-icons/fa"

export const GlobalFeaturesManager = () => {
  const {
    definitions,
    global,
    definitionsLoading,
    globalLoading,
    definitionsError,
    globalError,
    refreshAdminData,
    toggleGlobalFeature,
  } = useFeatureToggles()

  const [updatingFeatures, setUpdatingFeatures] = useState<Set<string>>(new Set())
  const hasLoadedRef = useRef(false)

  // Combine definitions with global states
  const featuresWithStates = definitions.map((definition) => {
    const globalState = global.find((g) => g.featureKey === definition.featureKey)
    return {
      ...definition,
      enabled: globalState?.enabled ?? definition.defaultEnabled,
      updatedAt: globalState?.updatedAt,
      updatedBy: globalState?.updatedBy,
    }
  })

  const handleToggleFeature = useCallback(
    async (featureKey: string, newEnabled: boolean) => {
      setUpdatingFeatures((prev) => new Set(prev).add(featureKey))

      try {
        await toggleGlobalFeature(featureKey, newEnabled)
        toaster.success({
          title: "Feature updated",
          description: `${featureKey} is now ${newEnabled ? "enabled" : "disabled"}`,
        })
      } catch (error) {
        console.error("Failed to update feature:", error)
        toaster.error({
          title: "Failed to update feature",
          description: error instanceof Error ? error.message : "Unknown error",
        })
      } finally {
        setUpdatingFeatures((prev) => {
          const newSet = new Set(prev)
          newSet.delete(featureKey)
          return newSet
        })
      }
    },
    [toggleGlobalFeature]
  )

  const handleRefresh = useCallback(() => {
    refreshAdminData()
  }, [refreshAdminData])

  const loading = definitionsLoading || globalLoading
  const error = definitionsError || globalError

  // Load data on component mount
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      refreshAdminData()
    }
  }, [refreshAdminData])

  if (error) {
    return (
      <VStack gap={4}>
        <Alert status="error" title="Error">
          Failed to load global features: {error}
        </Alert>
        <Button onClick={handleRefresh} variant="outline">
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Stack gap={6}>
      <HStack justify="space-between" align="center">
        <VStack align="start" gap={1}>
          <Text fontSize="lg" fontWeight="semibold">
            Global Feature Toggles
          </Text>
          <Text fontSize="sm" color="gray.600">
            Platform-level feature controls that affect all organizations
          </Text>
        </VStack>
        <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
          {loading ? <Spinner size="sm" /> : "Refresh"}
        </Button>
      </HStack>

      <Alert status="info" startElement={<FaInfoCircle />} title="Global Feature Toggles">
        These settings control features across the entire platform. Organizations can override these settings for
        features that allow organization-level overrides.
      </Alert>

      {loading && featuresWithStates.length === 0 ? (
        <Center py={8}>
          <HStack gap={3}>
            <Spinner size="lg" />
            <Text>Loading global features...</Text>
          </HStack>
        </Center>
      ) : (
        <Box overflowX="auto">
          <Table.Root size="sm" variant="outline">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Feature</Table.ColumnHeader>
                <Table.ColumnHeader>Category</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Org Override</Table.ColumnHeader>
                <Table.ColumnHeader>Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {featuresWithStates.map((feature) => {
                const isUpdating = updatingFeatures.has(feature.featureKey)

                return (
                  <Table.Row key={feature.id}>
                    <Table.Cell>
                      <VStack align="start" gap={1}>
                        <Text fontWeight="medium">{feature.name}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {feature.description}
                        </Text>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono">
                          {feature.featureKey}
                        </Text>
                      </VStack>
                    </Table.Cell>

                    <Table.Cell>
                      <Badge variant="subtle" colorPalette="blue">
                        {feature.category}
                      </Badge>
                    </Table.Cell>

                    <Table.Cell>
                      <HStack gap={2}>
                        {feature.enabled ? (
                          <Badge variant="solid" colorPalette="green">
                            <FaToggleOn />
                            Enabled
                          </Badge>
                        ) : (
                          <Badge variant="solid" colorPalette="red">
                            <FaToggleOff />
                            Disabled
                          </Badge>
                        )}
                        {isUpdating && <Spinner size="sm" />}
                      </HStack>
                    </Table.Cell>

                    <Table.Cell>
                      <Badge
                        variant={feature.allowOrgOverride ? "solid" : "outline"}
                        colorPalette={feature.allowOrgOverride ? "green" : "gray"}
                      >
                        {feature.allowOrgOverride ? "Allowed" : "Not Allowed"}
                      </Badge>
                    </Table.Cell>

                    <Table.Cell>
                      <HStack gap={2}>
                        <Switch
                          checked={feature.enabled}
                          disabled={isUpdating}
                          onCheckedChange={(details) => handleToggleFeature(feature.featureKey, details.checked)}
                          colorPalette={feature.enabled ? "green" : "red"}
                        />
                        <Text fontSize="xs" color="gray.500">
                          {feature.enabled ? "Disable" : "Enable"}
                        </Text>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      {featuresWithStates.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <Text color="gray.500">No global features found</Text>
          <Button onClick={handleRefresh} variant="outline" size="sm" mt={2}>
            Refresh
          </Button>
        </Box>
      )}
    </Stack>
  )
}
